import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../App";
import { getInlineLoader } from "../../Helpers";
import AOS from "aos";
import "aos/dist/aos.css";
import { IoMdArrowDropright } from "react-icons/io";
import { IoShieldCheckmarkOutline, IoLayersOutline, IoPulseOutline } from "react-icons/io5";

export default function SystemsFeatures() {
  const { navTo } = useContext(AppContext);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
    AOS.init({
      duration: 800,
      easing: "ease-out-cubic",
      once: false,
      offset: 50
    });
  }, []);

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  const features = [
    {
      icon: <IoLayersOutline className="p-3" size={64} />,
      title: "BizSuite Architecture",
      desc: "High-level enterprise logic designed around complex institutional workflows for absolute operational harmony.",
      color: "var(--secondaryColor)"
    },
    {
      icon: <IoPulseOutline className="p-3" size={64} />,
      title: "Scalable Financial Interop",
      desc: "Advanced payment integration and fiscal engines built to handle high-velocity enterprise transactions.",
      color: "#f4911e"
    },
    {
      icon: <IoShieldCheckmarkOutline className="p-3" size={64} />,
      title: "SSID & Secure Governance",
      desc: "Robust Single Sign-On Identity frameworks coupled with enterprise-grade encryption for total data integrity.",
      color: "var(--primaryColor)"
    },
  ];

  return (
    <section className="systems-features position-relative overflow-hidden py-5" style={{ 
      background: 'linear-gradient(180deg, #ffffff 0%, #f4f7fa 100%)',
      minHeight: '80vh'
    }}>
      
      {/* Subtle Background Pattern */}
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{ 
        zIndex: 0, 
        pointerEvents: 'none',
        backgroundImage: `radial-gradient(circle at 1px 1px, var(--secondaryColor) 0.04, transparent 0)`,
        backgroundSize: '40px 40px',
        opacity: 0.3
      }}></div>
      
      <div className="container position-relative" style={{ zIndex: 1, maxWidth: '1200px' }}>
        
        {/* Header Section */}
        <div className="row justify-content-center mb-5 pt-4">
          <div className="col-lg-8 text-center">
            <h6 className="text-primary fw-bold text-uppercase mb-3 tracking-widest" data-aos="fade-up">Enterprise Solutions</h6>
            <h2 className="font-bold mb-4" data-aos="fade-up" style={{ 
              fontSize: 'clamp(2.5rem, 5vw, 3.2rem)', 
              color: 'var(--primaryColor)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              fontWeight: '800'
            }}>
              Engineered for <span style={{ color: 'var(--secondaryColor)' }}>Institutional</span> <br/>
              Excellence
            </h2>

            <p className="lead mx-auto mb-0 text-muted" data-aos="fade-up" data-aos-delay="100" style={{ 
              fontSize: '1.1rem',
              maxWidth: '700px',
              lineHeight: 1.7
            }}>
              Deploying elite **Enterprise Solutions** through our flagship BizSuite ecosystem. We focus on fiscal interoperability, centralized SSID governance, and high-level business logic.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="row g-4 mb-5" data-aos="fade-up" data-aos-delay="200">
          {features.map((item, idx) => (
            <div key={idx} className="col-lg-4">
              <div className="feature-card h-100 p-5 rounded-1 position-relative bg-white shadow-sm" style={{
                border: '1px solid #eef2f7',
                transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
                zIndex: 1
              }}>
                {/* Icon Bubble */}
                <div className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle" style={{
                  width: '70px',
                  height: '70px',
                  background: `${item.color}10`,
                  color: item.color,
                  border: `1px solid ${item.color}25`,
                }}>
                  {item.icon}
                </div>

                <h3 className="font-bold mb-3 text-primary" style={{ 
                  fontSize: '1.25rem', 
                  letterSpacing: '-0.01em'
                }}>
                  {item.title}
                </h3>
                
                <p className="mb-0 text-muted" style={{ 
                  fontSize: '0.95rem',
                  lineHeight: 1.8
                }}>
                  {item.desc}
                </p>

                {/* Bottom Accent Line */}
                <div className="position-absolute bottom-0 start-0 w-100" style={{
                  height: '4px',
                  background: item.color,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.4s ease'
                }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="row justify-content-center" data-aos="fade-up" data-aos-delay="300">
          <div className="col-auto">
            <button 
              onClick={() => navTo({ item: "contact_us" })}
              className="btn rounded-1 px-5 py-3 fw-bold text-uppercase d-inline-flex align-items-center gap-2"
              style={{ 
                background: 'var(--primaryColor)', 
                color: 'white',
                fontSize: '0.85rem',
                letterSpacing: '1px',
                transition: 'all 0.3s ease',
                boxShadow: '0 10px 20px rgba(26,26,46,0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.background = 'var(--secondaryColor)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.background = 'var(--primaryColor)';
              }}
            >
              Request Quote <IoMdArrowDropright size={20} />
            </button>
          </div>
        </div>

      </div>

      {/* Premium Hover Effects CSS */}
      <style>{`
        .feature-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 25px 50px rgba(0,0,0,0.06) !important;
          border-color: transparent !important;
        }
        .feature-card:hover .position-absolute.bottom-0.start-0.w-100 {
          transform: scaleX(1);
        }
        @media (max-width: 767px) {
          .p-5 { padding: 2.5rem 1.5rem !important; }
        }
      `}</style>
    </section>
  );
}